/* eslint-disable no-use-before-define */
/* eslint-disable no-empty */

import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Selection, selectAll, select, axisLeft, scaleLinear, axisBottom, symbol, symbolTriangle } from 'd3';
import { darken, transparentize } from 'polished';
//
import {
  boxPlotClassName,
  testIdBoxPlot,
  testIdBoxPlotLegend,
  testIdBoxPlotItem,
  testIdBoxPlotNoData,
} from 'components/visuals/box-plot/constants';
import { ILineGraphItem, ISummaryForecast, ISummaryFormula } from 'store/deal-evaluation/types';
import { EDisplay } from 'components/deal-results/product-items';
import colors from 'theme/colors';
import { formatNumber } from 'lib/helpers/currency';
import font from 'theme/visuals';
import {
  itemHeight,
  horizontalPadding,
  whiskerHeight,
  strokeWidth,
  labelGapToYAxis,
  textAnchor,
  whiskerThickness,
  boxHeight,
  averageCircleRadius,
  averageCircleStrokeWidth,
  tooltipWidth,
  tooltipHeight,
  tooltipArrowHeight,
  tooltipVerticalOffset,
  tooltipPadding,
  dimensions,
  maxLeftMargin,
  minLeftMargin,
  margin,
  dataTestId,
  fontFamily,
} from 'constants/numericalValues';

export interface IBoxPlotProps {
  lineGraphItems: ILineGraphItem[];
  legendText: string;
  selectedDisplay: EDisplay;
  pricePerUnitLabel: string;
  productItemId: string;
}
const NdigitsMinValue = 2;
const NdigitsMidValue = 10;
const NdigitsMaxValue = 100;
const dimensionsMathMaxValue = 1.75;
const itemHeightConstValue = 0.25;
const itemHeightValue = 0.5;
const svgAttrConst = 2;
const tickPaddingValue = 6;
const tickArgumentsValue = 5;
const itemGroupYAttrValue = -4;
const itemGroupYAttrConst = 13;
const triangleSizeConst = 128;
const tooltipTransparentizeConst = 0.2;
const tooltipFontWeightConst = 600;

const CalculateMinMaxDollarAmounts = (lineGraphItems: ILineGraphItem[], selectedDisplay: EDisplay) => {
  // Calculate min/max dollar amounts (lowest p5, highest p95, add padding)
  let xMin = 0;
  let xMax = 0;

  // handle no data
  let dataFound = false;

  lineGraphItems.forEach(item => {
    const selectedPriceSummary = item.priceSummary[selectedDisplay];

    // handle no data
    if (!selectedPriceSummary) {
      return;
    }

    dataFound = true;

    let selectedPriceSummaryData: any = selectedPriceSummary.total;
    if (!selectedPriceSummary.total) {
      selectedPriceSummaryData = selectedPriceSummary; // fix issue with trying to access `total` on ISummaryPrice
    }
    const { p5, p95 } = selectedPriceSummaryData;
    if (!p5 || !p95) {
      return;
    }
    if (p5 < xMin || !xMin) {
      xMin = p5;
    }
    if (p95 > xMax || !xMax) {
      xMax = p95;
    }
  });

  return { dataFound, xMin, xMax };
};

const checkAveP5P25AndP50NegativeValue = (selectedPriceSummaryData: ISummaryForecast) => {
  const { ave, p5, p25, p50 } = selectedPriceSummaryData;
  return !ave || !p5 || !p25 || !p50;
};

const checkP75AndP95NegativeValue = (selectedPriceSummaryData: ISummaryForecast) => {
  const { p75, p95 } = selectedPriceSummaryData;
  return !p75 || !p95;
};

const checkAveP5P25P50P75AndP95NegativeValue = (selectedPriceSummaryData: ISummaryForecast) => {
  return (
    checkAveP5P25AndP50NegativeValue(selectedPriceSummaryData) || checkP75AndP95NegativeValue(selectedPriceSummaryData)
  );
};

const getTooltipBottomValue = (index: number, tooltipArrowHeightParam: number, tooltipHeightParam: number) => {
  return index === 0 ? tooltipArrowHeightParam : 0 - (tooltipHeightParam + tooltipArrowHeightParam);
};

const checkLargestValue = (largest: number) => {
  return largest < NdigitsMaxValue ? 1 : 0;
};

const getNdigits = (largest: number) => {
  return largest < NdigitsMidValue ? NdigitsMinValue : checkLargestValue(largest);
};

const setLeftMarginOfBoxPlot = (
  boxPlotRef: React.RefObject<HTMLDivElement>,
  minLeftMarginParam: number,
  maxLeftMarginParam: number,
  labelGapToYAxisParam: number,
  leftMargin: number,
  setLeftMargin: Function
) => {
  // After the graph has been rendered, we query the DOM for the actual space needed to fit the
  // display labels into the left margin. Then we update the left margin, which triggers a rerender
  // of the box-plot
  const currentBoxPlot = boxPlotRef.current;
  if (currentBoxPlot) {
    const textAreas = Array.from(currentBoxPlot.getElementsByClassName('boxplot-display-label'));
    let requiredLeftMargin = minLeftMarginParam;
    textAreas.forEach((txt: Element) => {
      // The try-catch block was added to ensure Jest test suite works and Jest TypeScript complaints disappear.
      // The getComputedTextLength() method exists in the browser but it does not exist in JSDOM (because JSDOM
      // has not implemented the browser's layout engine)
      try {
        const txtLength = (txt as SVGTextElement).getComputedTextLength() + labelGapToYAxisParam + NdigitsMinValue;
        if (txtLength > requiredLeftMargin) {
          requiredLeftMargin = txtLength;
        }
        // eslint-disable-next-line no-empty
      } catch {}
    });

    if (requiredLeftMargin > maxLeftMarginParam) {
      requiredLeftMargin = maxLeftMarginParam;
    }

    if (requiredLeftMargin !== leftMargin) {
      setLeftMargin(requiredLeftMargin);
    }
  }
};

const checkIndexForDrawBox = (index: number) => {
  return index !== 0;
};

const checkIndexForDrawTooltip = (index: number) => {
  return index === 0;
};

const getSelectedPriceSummaryData = (selectedPriceSummary: ISummaryFormula) => {
  let selectedPriceSummaryData: any = selectedPriceSummary.total;
  if (!selectedPriceSummaryData) {
    selectedPriceSummaryData = selectedPriceSummary;
  }
  return selectedPriceSummaryData;
};

/**
 * Receives the data all ready to render
 */

const createBoxPlotContainer = (boxPlotRef: RefObject<HTMLDivElement>, width: number, height: number) => {
  return select(boxPlotRef.current)
    .append('svg')
    .style('font-size', font.size)
    .style(fontFamily, font.family)
    .attr('width', width + margin.right + margin.left)
    .attr('height', height + margin.bottom + margin.top)
    .attr(dataTestId, testIdBoxPlot)
    .append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`);
};

const calculateLeftColumnTooltip = (
  tooltip: Selection<SVGGElement, unknown, null, undefined>,
  textColumnLeftX: number,
  currentYArg: number,
  textSpacing: number,
  item: ILineGraphItem
) => {
  let currentY = currentYArg;
  tooltip
    .append('circle')
    .attr('cx', textColumnLeftX - tickArgumentsValue)
    .attr('cy', currentY - tooltipVerticalOffset)
    .attr('r', svgAttrConst)
    .attr('fill', darken(horizontalPadding, item.color))
    .attr('stroke', 'white')
    .attr(strokeWidth, averageCircleStrokeWidth);
  tooltip
    .append('text')
    .attr('x', textColumnLeftX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .style('font-weight', tooltipFontWeightConst)
    .text('Average');
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnLeftX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .text('5%');
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnLeftX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .text('25%');
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnLeftX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .text('Median');
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnLeftX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .text('75%');
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnLeftX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .text('95%');
  return currentY;
};

const calculateRightColumnTooltip = (
  tooltip: Selection<SVGGElement, unknown, null, undefined>,
  textColumnRightX: number,
  currentYArg: number,
  ndigits: number,
  textSpacing: number,
  priceSummaryData: {
    ave: number;
    p5: number;
    p25: number;
    p50: number;
    p75: number;
    p95: number;
  }
) => {
  let currentY = currentYArg;
  tooltip
    .append('text')
    .attr('x', textColumnRightX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .attr(textAnchor, 'end')
    .style('font-weight', tooltipFontWeightConst)
    .text(formatNumber(priceSummaryData.ave, ndigits));
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnRightX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .attr(textAnchor, 'end')
    .text(formatNumber(priceSummaryData.p5, ndigits));
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnRightX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .attr(textAnchor, 'end')
    .text(formatNumber(priceSummaryData.p25, ndigits));
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnRightX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .attr(textAnchor, 'end')
    .text(formatNumber(priceSummaryData.p50, ndigits));
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnRightX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .attr(textAnchor, 'end')
    .text(formatNumber(priceSummaryData.p75, ndigits));
  currentY += textSpacing;
  tooltip
    .append('text')
    .attr('x', textColumnRightX)
    .attr('y', currentY)
    .attr('fill', 'white')
    .attr(textAnchor, 'end')
    .text(formatNumber(priceSummaryData.p95, ndigits));
};

const BoxPlot = ({ lineGraphItems, legendText, selectedDisplay, pricePerUnitLabel, productItemId }: IBoxPlotProps) => {
  const [leftMargin, setLeftMargin] = useState(minLeftMargin);
  const boxPlotRef = useRef<HTMLDivElement>(null);
  // generate unique class name for svg container
  const uniqueBoxPlotClassName = `${boxPlotClassName}-${productItemId}`;
  useEffect(() => {
    // Calculate dimensions
    if (boxPlotRef?.current) {
      dimensions.width = boxPlotRef.current.offsetWidth;
      // Height has to be enough to fit the tooltip.
      dimensions.height =
        itemHeight * (Math.max(dimensionsMathMaxValue, lineGraphItems.length) + itemHeightConstValue) +
        margin.bottom +
        margin.top;
      margin.left = leftMargin;
    }

    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Clear old SVG
    selectAll(`.${uniqueBoxPlotClassName} > *`).remove();

    // Create container
    const svg = createBoxPlotContainer(boxPlotRef, width, height);

    // Set the ranges
    const x = scaleLinear().range([0, width]);
    const y = scaleLinear().range([height, 0]);

    // Calculate min/max dollar amounts (lowest p5, highest p95, add padding)
    const { dataFound, xMin, xMax } = CalculateMinMaxDollarAmounts(lineGraphItems, selectedDisplay);

    // handle no data
    if (!dataFound) {
      svg
        .append('text')
        .text('No data')
        .attr(dataTestId, testIdBoxPlotNoData)
        .attr('x', width / svgAttrConst)
        .attr('y', height / svgAttrConst);
    }

    // Add horizonal padding
    const horizontalPaddingValue = horizontalPadding * Math.abs(xMax - xMin);

    // Set domain
    x.domain([xMin - horizontalPaddingValue, xMax + horizontalPaddingValue]);

    // X-axis
    svg
      .append('g')
      .attr('transform', `translate(0, ${y(0)})`)
      .style('font-size', font.size)
      .style(fontFamily, font.family)
      .call(
        axisBottom(x)
          .tickPadding(tickPaddingValue)
          .tickArguments([tickArgumentsValue])
          .tickFormat(d => `${typeof d === 'number' ? formatNumber(d, 0) : d.valueOf} ${pricePerUnitLabel}`)
      );

    // Y-axis
    svg
      .append('g')
      .style('font-size', font.size)
      .style(fontFamily, font.family)
      .call(
        axisLeft(y)
          .tickFormat(() => '')
          .tickSize(0)
      );

    // Draw legend text
    svg
      .append('text')
      .attr('x', 0)
      .attr('y', height + margin.bottom - whiskerHeight)
      .attr(dataTestId, testIdBoxPlotLegend)
      .text(legendText);

    // Declare tooltip references, so mouseover/out events can be attached before
    // the tooltip is defined.
    const tooltips: Selection<SVGGElement, unknown, null, undefined>[] = [];

    // Draw boxes
    // Offset first item by 50% so the top of the first box plot does not exceed
    // the y-axis. An additional 25% to create some "padding" versus the y-axis.
    let currentLineItemYValue = itemHeight * (itemHeightValue + itemHeightConstValue);
    lineGraphItems.forEach((item, index) => {
      const selectedPriceSummary = item.priceSummary[selectedDisplay];
      if (!selectedPriceSummary) {
        return;
      }

      const { ave, p5, p25, p50, p75, p95 } = getSelectedPriceSummaryData(selectedPriceSummary);

      if (checkAveP5P25P50P75AndP95NegativeValue(getSelectedPriceSummaryData(selectedPriceSummary))) {
        return;
      }

      const itemGroup = svg
        .append('g')
        .attr(dataTestId, testIdBoxPlotItem)
        .attr('class', 'box-plot-item-group')
        .attr('transform', `translate(0, ${currentLineItemYValue})`);

      // draw item separator (top -- except for first element)
      if (checkIndexForDrawBox(index)) {
        itemGroup
          .append('line')
          .attr('class', 'item-separator')
          .attr('x1', 0 - margin.left)
          .attr('y1', 0 - itemHeight / svgAttrConst)
          .attr('x2', width)
          .attr('y2', 0 - itemHeight / svgAttrConst)
          .style(strokeWidth, 1)
          .style('stroke', 'rgba(209, 209, 214, 0.35)');
      }

      // draw labels
      itemGroup
        .append('text')
        .attr('x', -labelGapToYAxis)
        .attr('y', itemGroupYAttrValue)
        .attr(textAnchor, 'end')
        .text(item.label);

      itemGroup
        .append('text')
        .attr('class', 'boxplot-display-label')
        .attr('x', -labelGapToYAxis)
        .attr('y', itemGroupYAttrConst)
        .attr(textAnchor, 'end')
        .text(item.priceSummary.description);

      // draw main whisker line
      const graphItem = itemGroup
        .append('g')
        .attr('class', 'box-plot-graph-item')
        .on('mouseover', () => {
          tooltips[index].style('display', null);
        })
        .on('mouseout', () => {
          tooltips[index].style('display', 'none');
        });

      graphItem
        .append('line')
        .attr('class', 'whisker')
        .attr('x1', x(p5))
        .attr('y1', 0)
        .attr('x2', x(p95))
        .attr('y2', 0)
        .style(strokeWidth, whiskerThickness)
        .style('stroke', item.color)
        .attr('class', 'whisker-trunk');

      // draw whisker end cap (start)
      graphItem
        .append('line')
        .attr('x1', x(p5))
        .attr('y1', 0 - whiskerHeight / svgAttrConst)
        .attr('x2', x(p5))
        .attr('y2', 0 + whiskerHeight / svgAttrConst)
        .style(strokeWidth, svgAttrConst)
        .style('stroke', item.color)
        .attr('class', 'whisker-cap');

      // draw whisker end cap (end)
      graphItem
        .append('line')
        .attr('x1', x(p95))
        .attr('y1', 0 - whiskerHeight / svgAttrConst)
        .attr('x2', x(p95))
        .attr('y2', 0 + whiskerHeight / svgAttrConst)
        .style(strokeWidth, svgAttrConst)
        .style('stroke', item.color)
        .attr('class', 'whisker-cap');

      // draw primary box region
      graphItem
        .append('polygon')
        .attr(
          'points',
          `
          ${x(p25)} ${0 - boxHeight / svgAttrConst}
          ${x(p75)} ${0 - boxHeight / svgAttrConst}
          ${x(p75)} ${0 + boxHeight / svgAttrConst}
          ${x(p25)} ${0 + boxHeight / svgAttrConst}
        `
        )
        .attr('fill', item.color);

      // draw median line
      graphItem
        .append('line')
        .attr('x1', x(p50))
        .attr('y1', 0 - boxHeight / svgAttrConst)
        .attr('x2', x(p50))
        .attr('y2', 0 + boxHeight / svgAttrConst)
        .style(strokeWidth, svgAttrConst)
        .style('stroke', 'white');

      // draw average circle
      graphItem
        .append('circle')
        .attr('cx', x(ave))
        .attr('cy', 0)
        .attr('r', averageCircleRadius)
        .attr('fill', darken(horizontalPadding, item.color))
        .attr('stroke', 'white')
        .attr(strokeWidth, averageCircleStrokeWidth);

      // increment y-value
      currentLineItemYValue += itemHeight;
    });

    // Draw tooltip: tooltips are drawn last to ensure they are on top.
    currentLineItemYValue = itemHeight * (itemHeightValue + itemHeightConstValue);
    lineGraphItems.forEach((item, index) => {
      const itemTooltipId = `item-${index}-tooltip`;
      const selectedPriceSummary = item.priceSummary[selectedDisplay];
      if (!selectedPriceSummary) {
        return;
      }

      const { ave, p5, p25, p50, p75, p95 } = getSelectedPriceSummaryData(selectedPriceSummary);
      const itemGroup = svg
        .append('g')
        .attr(dataTestId, testIdBoxPlotItem)
        .attr('class', 'box-plot-item-group')
        .attr('transform', `translate(0, ${currentLineItemYValue})`);

      const tooltip = itemGroup
        .append('g')
        .attr('id', itemTooltipId)
        .attr('class', 'box-plot-tooltip')
        .attr('x', x(ave) - tooltipWidth / svgAttrConst)
        .attr('y', 0 - (tooltipHeight + whiskerHeight))
        .style('display', 'none');

      const triangle = tooltip
        .append('path')
        .attr(
          'd',
          symbol()
            .type(symbolTriangle)
            .size(triangleSizeConst)
        )
        .attr('fill', transparentize(tooltipTransparentizeConst, colors.darkBackgroundElevation1));

      if (checkIndexForDrawTooltip(index)) {
        triangle.attr('transform', `translate(${x(ave)}, 24)`);
      } else {
        triangle.attr('transform', `translate(${x(ave)}, -24) rotate(180)`);
      }

      const tooltipBottom = getTooltipBottomValue(index, tooltipArrowHeight, tooltipHeight);

      tooltip
        .append('rect')
        .attr('width', tooltipWidth)
        .attr('height', tooltipHeight)
        .attr('fill', transparentize(tooltipTransparentizeConst, colors.darkBackgroundElevation1))
        .attr('x', x(ave) - tooltipWidth / svgAttrConst)
        .attr('y', tooltipBottom)
        .attr('rx', tooltipVerticalOffset)
        .attr('ry', tooltipVerticalOffset);

      // avg, 5%, 25%, median, 75%, 95%
      const textColumnLeftX = x(ave) - tooltipWidth / svgAttrConst + tooltipPadding;
      const textColumnRightX = x(ave) + tooltipWidth / svgAttrConst - tooltipPadding;
      let currentY = tooltipPadding + tooltipVerticalOffset + tooltipBottom;
      const textSpacing = 16;
      let currentYArg = currentY;
      // left column
      calculateLeftColumnTooltip(tooltip, textColumnLeftX, currentYArg, textSpacing, item);

      // right column
      currentY = tooltipPadding + tooltipVerticalOffset + tooltipBottom;
      const largest = Math.max(Math.abs(p5), Math.abs(p95));
      const ndigits = getNdigits(largest);
      currentYArg = currentY;
      calculateRightColumnTooltip(tooltip, textColumnRightX, currentYArg, ndigits, textSpacing, {
        ave,
        p5,
        p25,
        p50,
        p75,
        p95,
      });

      // increment y-value
      currentLineItemYValue += itemHeight;

      // Used as reference for mouseover/out events on box-plot graph
      tooltips.push(tooltip);
    });
  }, [lineGraphItems, legendText, selectedDisplay, pricePerUnitLabel, uniqueBoxPlotClassName, leftMargin]);

  useEffect(() => {
    // After the graph has been rendered, we query the DOM for the actual space needed to fit the
    // display labels into the left margin. Then we update the left margin, which triggers a rerender
    // of the box-plot
    setLeftMarginOfBoxPlot(boxPlotRef, minLeftMargin, maxLeftMargin, labelGapToYAxis, leftMargin, setLeftMargin);
  }, [leftMargin]);

  return <div className={uniqueBoxPlotClassName} ref={boxPlotRef} />;
};

export default BoxPlot;
